import { Paper, Toolbar, Typography, TextField, Box } from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import { Star } from "@mui/icons-material";
import React, { ReactNode, useContext, useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { ProjectDetails } from "../../../redux/types/firebase";
import {selectModeData, selectProject, selectUserId} from "../../../redux/selectors/editor";
import { selectDictionary, selectGrammar, selectIgnoredGrammar, selectLocks } from "../../../redux/selectors/firebase";
import { ICharacterData, IScriptElement, ScriptElementData, IParagraphData, INoteData } from "../../script/parseScript";
import ScriptRender, { ActiveTags, ScriptTypeRenderers } from "../../writer/ScriptRender";
import { ScriptTextRender } from "../../writer/ScriptTextRenderer";
import { Grammar, IgnoredGrammar } from "../../writer/Writer";
import { FirebaseContext } from "../../firebase/FirebaseProvider";
import { Locks } from "../../navbar/StateLock";
import {IGraphData, UpdateModeData, updateModeData} from "../../../redux/slices/editor";

const useStyles = makeStyles(theme => ({
    avatar: {
        height: theme.spacing(3),
        width: theme.spacing(3)
    },
    icon: {
        marginRight: theme.spacing(1)
    },
    toolbar: {
        borderBottom: 'solid 1px lightgrey',
        justifyContent: "space-between",
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
        minHeight: "32px"
    },
    title: {
        flexGrow: 1,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    script: {
        display: "-webkit-box",
        "-webkit-line-clamp": 8,
        "-webkit-box-orient": "vertical",
        overflow: "hidden",
        margin: theme.spacing(1),
        wordBreak: "break-word",
        minHeight: "60px"
    },
    quickViewScript: {
        wordBreak: "break-word",
        position: "relative",
        overflowY: "scroll",
        height: "100%",
        '&::-webkit-scrollbar': {
            width: '0.4em'
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,.1)',
            borderRadius: '0.4em'
        },
        zIndex: "inherit"
    },
    quickViewContainer: {
        position: "absolute",
        padding: "8px",
        width: "300px",
        height: "400px",
        zIndex: theme.zIndex.modal,
        cursor: "default"
    },
    quickViewPaper: {
        width: "100%",
        height: "100%",
        fontFamily: theme.typography.fontFamily,
        display: "flex",
        flexDirection: "column",
        zIndex: "inherit"
    },
    textField: {
        boxSizing: "border-box",
        maxWidth: "100%",
        padding: theme.spacing(1),
        "& .MuiInputBase-multiline": {
            padding: 0,
        },
        "& .MuiInputBase-inputMultiline": {
            lineHeight: 1.5,
        },
        zIndex: "inherit"
    },
    textView: {
        padding: theme.spacing(1),
        zIndex: "inherit"
    }
}));

const useScriptStyles = makeStyles(theme => ({
    avatar: {
        height: theme.spacing(8),
        width: theme.spacing(8),
    },
    character: {
        fontFamily: 'inherit',
        fontWeight: "bold",
        textAlign: "center",
    },
    gesture: {
        fontFamily: 'inherit',
    },
    heading: {
        fontFamily: 'inherit',
        fontWeight: "bold",
        textTransform: "uppercase",
    },
    line: {
        fontFamily: 'inherit',
    },
    note: {
        marginTop: 12,
        marginBottom: 12,
    },
    option: {
        fontFamily: 'inherit',
    },
    state: {
        fontFamily: 'courier',
        marginTop: theme.spacing(2),
        marginLeft: theme.spacing(12),
        marginRight: theme.spacing(12),
        padding: theme.spacing(1),
        borderRadius: "6px",
        borderStyle: "solid",
        borderColor: "transparent",
        borderWidth: "1px"
    },
    highlighted: {
        backgroundColor: "rgba(1,1,1,0.04)"
    },
    title: {
        fontFamily: "inherit",
        fontSize: "large",
        fontWeight: "bold",
        marginBottom: 12,
        textAlign: "center",
    },
    textHighlight: {
        background: "yellow",
        position: "absolute",
        zIndex: -1
    },
    comment: {
        backgroundColor: theme.palette.comment.main
    },
    commentHighlight: {
        backgroundColor: theme.palette.commentHighlight.main
    },
    grammar: {
        textDecorationColor: theme.palette.grammar.main,
        textDecorationLine: "underline",
        textDecorationStyle: "double",
        textDecorationWidth: 1
    },
    search: {
        backgroundColor: theme.palette.search.main
    },
    spelling: {
        textDecorationColor: theme.palette.spelling.main,
        textDecorationLine: "underline",
        textDecorationStyle: "double",
        textDecorationWidth: 1
    }
}));
interface IProps
{
    id: string
    projectDetails: ProjectDetails
    data: {
        label: string
        script: string
        initial: string
    }
    dictionary: { [word: string]: any }
    anchorEl: HTMLElement | null
    containerEl: React.RefObject<HTMLElement | null>
    grammar: Grammar
    graphData: IGraphData
    ignoredGrammar: IgnoredGrammar
    setAnchorEl: (element: HTMLElement | null) => void
    setShowTitle: (show: boolean) => void;
    icon?: ReactNode
    project: string
    content: IScriptElement[]
    locks?: Locks,
    updateModeData: UpdateModeData,
    user: string
}

function ScriptQuickView({id, content, data, dictionary, grammar, icon, ignoredGrammar, graphData, projectDetails,
                          project, setAnchorEl, setShowTitle, containerEl, anchorEl, locks, updateModeData, user}: IProps)
{
    const classes = useStyles();
    const scriptClasses = useScriptStyles();
    const quickViewEl = useRef<HTMLDivElement | null>(null);

    const [edited, setEdited] = useState<string>(data.script);

    const stateGrammar = (grammar && grammar[id]?.matches) || [];
    const stateIgnoredGrammar = (ignoredGrammar && id in ignoredGrammar) ? Object.values(ignoredGrammar[id]) : [];

    const bound = containerEl.current?.getBoundingClientRect();
    const { setStateMember } = useContext(FirebaseContext);

    let left: string = "0px";
    let top: string = "0px";

    let quickViewBound;

    if (anchorEl !== null && bound !== undefined && quickViewEl.current)
    {
        quickViewBound = quickViewEl.current?.getBoundingClientRect();
        if (quickViewBound)
        {
            left = `${(bound.width - quickViewBound.width) / 2}px`;
            top = `-60px`;
        }
    }

    const handlePointerLeave = () =>
    {
        if (graphData.quickViewEdit === undefined)
        {
            setAnchorEl(null);
            setShowTitle(true);
        }
    }

    const handleClickQuickView = (event: React.MouseEvent<HTMLElement>) =>
    {
        if (event.altKey)
        {
            updateModeData({quickViewEdit: id});
        }
    }

    const handleKeyDown = (event: KeyboardEvent) =>
    {
        if (graphData.quickViewEdit === id && event.ctrlKey)
        {
            if (data.script !== edited)
            {
                setStateMember(project, id, "script", edited);
            }
            updateModeData({quickViewEdit: undefined});
        } else if (event.key === "Escape")
        {
            updateModeData({quickViewEdit: undefined});
            event.stopPropagation();
        }
    }

    const handleScriptChange = ({ target }: React.ChangeEvent<HTMLInputElement>) =>
    {
        setEdited(target.value);
    };

    const handleClickFragment = () =>
    {
        return () => { }
    }
    const { trackGrammar, trackIgnoredGrammar } = useContext(FirebaseContext);

    useEffect(() =>
    {
        if (graphData.quickViewEdit === id)
        {
            window.addEventListener("keydown", handleKeyDown);
        }

        return () =>
        {
            window.removeEventListener("keydown", handleKeyDown);
        }
    });

    useEffect(() =>
    {
        trackGrammar(project);
        trackIgnoredGrammar(project);
    }, [project, trackGrammar, trackIgnoredGrammar]);

    const renderOption = (element: IScriptElement, key: string, index: number, activeTags: ActiveTags, textRender: ScriptTextRender) =>
    {
        const options = element.data as ScriptElementData[];
        const keyText = `${key}_${index}`;

        return (
            <Box key={keyText} display="flex" flexDirection="column">
                <ul>
                    {options.map((option, index) => (
                        <li key={index}>
                            {
                                textRender.renderAnnotated(option.condition ? `${option.text} (if ${option.condition})` : option.text,
                                    index, textRender.getStyleTags(option.text, textRender.tagOffset), activeTags)
                            }
                        </li>
                    ))}
                </ul>
            </Box>
        );
    };
    const renderers: ScriptTypeRenderers = {
        character: (element, key, index, styles, textRender) =>
        {
            const data = element.data as ICharacterData;
            const keyText = `${key}_${index}`;

            let scriptOffset = data.offset;
            const lines = data.lines.map((line, index) =>
            {
                const tags = textRender.getStyleTags(line, scriptOffset);

                scriptOffset += line.length + 1 // For the new line;
                return textRender.renderLine((line || '\u00A0') as string, index, tags, styles);
            });

            return (
                <Box key={keyText}>
                    <Typography className={scriptClasses.character}>{data.character}</Typography>
                    {data.parenthetical &&
                        <Typography className={scriptClasses.character}>
                            ({data.parenthetical})
                        </Typography>}
                    {lines}
                </Box>
            );
        },
        comment: (element: IScriptElement, key: string, index: number, styles, textRender) =>
            null,
        cut: (element: IScriptElement, key: string, index: number, styles, textRender) =>
        {
            const data = element.data as ScriptElementData;

            return (
                <Box key={`${key}_${index}`}>
                    {textRender.renderLine(data.text, 0, textRender.getStyleTags(data.text, 0), styles)}
                </Box>
            );
        },
        gesture: (element: IScriptElement, key: string, index: number, styles, textRender) =>
        {
            const gestures = element.data as ScriptElementData[];

            const keyText = `${key}_${index}`;

            return (
                <Box key={keyText} display="flex" flexDirection="column">
                    <ul>
                        {gestures.map((option, index) => (
                            <li key={index}>
                                {
                                    textRender.renderAnnotated(option.condition ? `${option.text} (if ${option.condition})` : option.text,
                                        index, textRender.getStyleTags(option.text, textRender.tagOffset), styles)
                                }
                            </li>
                        ))}
                    </ul>
                </Box>
            );
        },
        paragraph: (element: IScriptElement, key: string, index: number, styles, textRender) =>
        {
            const paragraphData = element.data as IParagraphData;
            const firstLine = paragraphData.lines.find(line => line.length > 0);
            let scriptOffset = data.script.indexOf(firstLine || "");
            const lines = paragraphData.lines.map((line, index) =>
            {
                const tags = textRender.getStyleTags(line, scriptOffset);

                scriptOffset += line.length + 1 // new line is a character;
                return textRender.renderLine((line || '\u00A0') as string, index, tags, styles);
            });

            return (
                <Box key={`${key}_${index}`}>
                    {lines}
                </Box>
            );
        },
        note: (element: IScriptElement, key: string, index: number, styles, textRender) =>
        {
            const data = element.data as INoteData;

            return (
                <Box className={scriptClasses.note} key={`${key}_${index}`} fontWeight="bold">
                    {textRender.renderAnnotated(`(${data.note}) - `, 0, textRender.getStyleTags(data.line, textRender.tagOffset), styles)}
                    {textRender.renderAnnotated(data.line, data.note.length, textRender.getStyleTags(data.line, 0), styles)}
                </Box>
            );
        },
        conditional: renderOption,
        option: renderOption,
        still: (element, key, index) =>
        {
            const data = element.data as ScriptElementData;

            return (
                <Box key={`${key}_${index}`} display="flex" flexDirection="column">
                    <Box flexGrow={1} style={{
                        backgroundImage: `url(${projectDetails.imageSource}/stills/${data.text})`,
                        backgroundPosition: "center",
                        backgroundSize: "cover",
                        height: 100
                    }} />
                </Box>
            );
        },
        title: (element, key, index, styles, textRender) =>
        {
            const data = element.data as ScriptElementData;

            return (
                <Box key={`${key}_${index}`}>
                    {textRender.renderLine(data.text, index, textRender.getStyleTags(data.text, 0), styles)}
                </Box >
            );
        },
    };

    return (anchorEl !== null && bound !== undefined) ? <div ref={quickViewEl} className={`${classes.quickViewContainer} nodrag nowheel`} style={{
        left: left,
        top: top,
        visibility: quickViewBound !== undefined ? "visible" : "hidden"
    }}><Paper elevation={10} className={classes.quickViewPaper} onClick={handleClickQuickView} onMouseLeave={handlePointerLeave}>
            <Toolbar disableGutters className={classes.toolbar}>
                {data.initial && <Star fontSize="small" className={classes.icon} />}
                {icon}
                <Typography className={classes.title} variant="body2">{data.label}</Typography>
            </Toolbar> <Box className={`${classes.quickViewScript} nowheel nodrag`}>
                {graphData.quickViewEdit === id ?
                    <TextField classes={{ root: classes.textField }}
                        value={edited} multiline fullWidth
                        onChange={handleScriptChange} disabled={(locks && id in locks) && locks[id].user !== user} />
                    : <Box className={classes.textView}><ScriptRender classes={scriptClasses}
                        state={id}
                        renderers={renderers}
                        content={content}
                        onClickFragment={handleClickFragment}
                        text={data.script}
                        highlightOptions={{
                            comments: [],
                            dictionary: Object.keys(dictionary || {}),
                            grammar: stateGrammar,
                            ignoredGrammar: stateIgnoredGrammar
                        }} /></Box>
                }
            </Box>
        </Paper>
    </div > : null
}

const mapStateToProps = (store: never) => ({
    dictionary: selectDictionary(store),
    grammar: selectGrammar(store),
    graphData: selectModeData(store) as IGraphData,
    ignoredGrammar: selectIgnoredGrammar(store),
    project: selectProject(store),
    locks: selectLocks(store),
    user: selectUserId(store)
});

export default connect(mapStateToProps, {
    updateModeData
})(ScriptQuickView);
